import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Camera, X, Crown, Clock } from "lucide-react";
import { T } from "../lib/i18n";

function fmtWait(seconds) {
  if (!seconds || seconds < 60) return T("any moment");
  const m = Math.round(seconds / 60);
  return `~${m} ${T("min")}`;
}

export default function PhotoQueueCard() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/queue/me");
      setMe(data);
    } catch (e) { console.debug("queue: status refresh failed", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
  }, [refresh]);

  const join = async () => {
    try {
      await api.post("/queue/join");
      toast.success(T("You're in! We'll let you know when it's almost your turn."));
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const leave = async () => {
    try {
      await api.delete("/queue/me");
      toast(T("You left the queue"), { icon: "👋" });
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return null;

  if (!me?.in_queue) {
    return (
      <div className="card-elev p-5 space-y-3 fadein" data-testid="queue-not-in">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-motif">{T("Photo with the couple")}</h3>
        </div>
        <p className="text-sm text-motif-muted">
          {T("Skip the line — join the virtual queue and we'll buzz you when it's almost your turn.")}
        </p>
        <button onClick={join} className="btn-primary w-full" data-testid="queue-join-btn">
          {T("Join the queue")}
        </button>
      </div>
    );
  }

  const entry = me.entry;
  const isUpNext = entry.status === "up_next";
  const isInProgress = entry.status === "in_progress";
  const isVip = (entry.priority_level || 0) > 0;

  return (
    <div className="card-elev p-5 space-y-3 fadein" data-testid="queue-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-motif">{T("Photo queue")}</h3>
        </div>
        {isVip && (
          <span className="text-[10px] tracking-luxe px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "var(--motif-primary)", color: "var(--motif-primary-fg)" }}>
            <Crown className="w-3 h-3" /> VIP
          </span>
        )}
      </div>
      <div className="text-center py-4">
        {isInProgress ? (
          <>
            <div className="text-[10px] tracking-luxe text-motif-muted">{T("it's your turn")}</div>
            <div className="font-display text-3xl text-primary-motif mt-1">{T("Smile!")} 📸</div>
          </>
        ) : isUpNext ? (
          <>
            <div className="text-[10px] tracking-luxe text-motif-muted">{T("you're up next")}</div>
            <div className="font-display text-3xl text-primary-motif mt-1">{T("Head over now")}</div>
          </>
        ) : (
          <>
            <div className="text-[10px] tracking-luxe text-motif-muted">{T("your position")}</div>
            <div className="font-display text-6xl text-primary-motif mt-1">{me.position}</div>
            <div className="text-xs text-motif-muted mt-1">{T("of")} {me.total}</div>
            <div className="flex items-center justify-center gap-1.5 text-xs text-motif-muted mt-2">
              <Clock className="w-3 h-3" />
              {fmtWait(me.estimated_wait_seconds)}
            </div>
          </>
        )}
      </div>
      {!isInProgress && (
        <button onClick={leave} className="btn-ghost text-xs w-full flex items-center justify-center gap-1.5" data-testid="queue-leave-btn">
          <X className="w-3.5 h-3.5" /> {T("Leave queue")}
        </button>
      )}
    </div>
  );
}
